import type { SaveSelfAssessmentResultsInput } from "@/graphql/generated/types";

// Helpers for the per-element self-assessment form.
//
// The UI uses a 5-point Likert scale. The backend still stores a 0-100
// `scorePercent` (SAM_RESULT.score), so each Likert level maps to a canonical
// percent here - this map is the SINGLE source of truth: switching to a native
// Likert field later only needs the migration to reuse `likertToPercent` /
// `percentToLikert`. `AnswerMap` holds the chosen Likert LEVEL (1..5).

export type AnswerMap = Record<string, number | undefined>;

export interface LikertLevel {
  level: number; // 1..5
  labelId: string; // i18n message id (resolve via intl) - see `likert.*`
  percent: number; // canonical 0-100 stored in SAM_RESULT.score
}

// 5 levels spread evenly across 0-100 (step 100/(5-1) = 25). Reversible.
export const LIKERT_LEVELS: readonly LikertLevel[] = [
  { level: 1, labelId: "likert.1", percent: 0 },
  { level: 2, labelId: "likert.2", percent: 25 },
  { level: 3, labelId: "likert.3", percent: 50 },
  { level: 4, labelId: "likert.4", percent: 75 },
  { level: 5, labelId: "likert.5", percent: 100 },
];

export function likertToPercent(level: number): number {
  return LIKERT_LEVELS.find((l) => l.level === level)?.percent ?? 50;
}

// Nearest Likert level for an existing 0-100 score (legacy/continuous data).
export function percentToLikert(percent: number): number {
  const clamped = Math.max(0, Math.min(100, percent));
  return Math.round(clamped / 25) + 1; // 0->1, 25->2, 50->3, 75->4, 100->5
}

export function buildSaveResultsInput(
  answers: AnswerMap,
  knownQuestionIds: readonly string[],
): SaveSelfAssessmentResultsInput {
  const results: { questionId: string; scorePercent: number }[] = [];
  for (const questionId of knownQuestionIds) {
    const level = answers[questionId];
    if (level === undefined) continue;
    // AnswerMap stores the Likert level; persist the mapped percent.
    results.push({ questionId, scorePercent: likertToPercent(level) });
  }
  return { results };
}

export function isAssessmentComplete(
  answers: AnswerMap,
  knownQuestionIds: readonly string[],
): boolean {
  return knownQuestionIds.every((id) => answers[id] !== undefined);
}
